<template>
  <li
    class="sf-list__item tw-group"
    :class="containerClasses"
    v-bind="modifiedAttrs"
    :data-testid="`sf-list-item-${props.itemId}`"
    @click.prevent.stop="handleClick"
  >
    <div class="sf-list__item-content sf-list__item-content--left">
      <a
        href="#"
        :class="linkClasses"
        data-testid="sf-item-left"
        @click.prevent.stop="handleLeftClick"
      >
        <div class="sf-list__icon">
          <slot name="icon" />
        </div>
        <span class="sf-list__text">
          <slot />
        </span>
      </a>
    </div>
    <div class="sf-list__item-content sf-list__item-content--right">
      <a
        href="#"
        class="sf-list__link"
        data-testid="sf-item-right"
        @click.prevent.stop="handleRightClick"
      >
        <slot name="right-content" />
      </a>
    </div>
  </li>
</template>

<script lang="ts" setup>
import { computed, useAttrs } from 'vue'

import { itemVariant, ItemVariants } from './constants'

// eslint-disable-next-line no-undef
defineOptions({
  name: 'SfListItem',
})

interface Props {
  cornered?: boolean
  selected?: boolean
  variant?: ItemVariants
  background?: 'default' | 'transparent'
  fontWeight?: 'normal' | 'medium' | 'bold'
  itemId: string
  defaultCursor?: boolean
}

type ClickEvent = {
  itemId: string
  side?: 'left' | 'right'
}

const props = withDefaults(defineProps<Props>(), {
  variant: itemVariant.tall,
  background: 'default',
  fontWeight: 'normal',
  defaultCursor: false,
})

const emit =
  defineEmits<{
    'item-click': [payload: ClickEvent]
  }>()

const $attrs = useAttrs()

const containerClasses = computed(() => {
  return {
    'tw-rounded-lg': props.cornered,
    'tw-border-0': props.cornered,
    'tw-bg-sf-primary-subtle': props.selected && props.background === 'default',
    'tw-text-sf-snow-lighter':
      props.selected && props.background === 'transparent',
    'tw-bg-sf-primary': props.selected && props.background === 'transparent',
    'hover:tw-bg-sf-primary-darker':
      props.selected && props.background === 'transparent',
    [`sf-list__item--${props.variant}`]: !!props.variant,
    'tw-bg-transparent': !props.selected && props.background === 'transparent',
    [`tw-font-${props.fontWeight}`]: !!props.fontWeight,
  }
})

const linkClasses = computed(() => {
  return {
    'sf-list__link': true,
    '!tw-cursor-default': props.defaultCursor,
  }
})

const modifiedAttrs = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { onClick, ...rest } = $attrs
  return rest
})

const handleClick = () => {
  emit('item-click', { itemId: props.itemId })
}

const handleLeftClick = () => {
  emit('item-click', { itemId: props.itemId, side: 'left' })
}

const handleRightClick = () => {
  emit('item-click', { itemId: props.itemId, side: 'right' })
}
</script>
